import { supabase } from '@/lib/supabaseClient';
import { 
  GanttProject, 
  GanttTask, 
  GanttDependency, 
  GanttResource,
  TaskFormData,
  ProjectFormData
} from '../types/gantt.types';

class GanttService {
  private baseUrl = import.meta.env.VITE_API_URL || 'https://api.plexisystem.pl';

  // ==================== PROJECTS ====================
  async getProjects(filters?: any): Promise<GanttProject[]> {
    try {
      console.log('📊 Fetching Gantt projects from Supabase...');
      
      let query = supabase
        .from('gantt_projects')
        .select('*')
        .order('created_at', { ascending: false });

      if (filters?.status) {
        query = query.eq('status', filters.status);
      }

      const { data, error } = await query;

      if (error) {
        console.error('Error fetching projects:', error);
        throw error;
      }

      console.log(`✅ Fetched ${data?.length || 0} projects`);
      return data || [];
    } catch (error) {
      console.error('Failed to fetch projects:', error);
      throw error;
    }
  }

  async createProject(projectData: ProjectFormData): Promise<GanttProject> {
    try {
      const { data, error } = await supabase
        .from('gantt_projects')
        .insert([{
          ...projectData,
          created_by: localStorage.getItem('userEmail') || 'system'
        }])
        .select()
        .single();

      if (error) throw error;
      console.log('✅ Project created:', data);
      return data;
    } catch (error) {
      console.error('Failed to create project:', error);
      throw error;
    }
  }

  async updateProject(id: string, projectData: Partial<ProjectFormData>): Promise<GanttProject> {
    try {
      const { data, error } = await supabase
        .from('gantt_projects')
        .update(projectData)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      console.log('✅ Project updated:', data);
      return data;
    } catch (error) {
      console.error('Failed to update project:', error);
      throw error;
    }
  }

  async deleteProject(id: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('gantt_projects')
        .delete()
        .eq('id', id);

      if (error) throw error;
      console.log('✅ Project deleted');
    } catch (error) {
      console.error('Failed to delete project:', error);
      throw error;
    }
  }

  // ==================== TASKS ====================
  async getTasks(projectId?: string, filters?: any): Promise<GanttTask[]> {
    try {
      console.log('📋 Fetching Gantt tasks from Supabase...');
      
      let query = supabase
        .from('gantt_tasks')
        .select('*')
        .order('order_index', { ascending: true });

      if (projectId) {
        query = query.eq('project_id', projectId);
      }

      if (filters?.status && filters.status.length > 0) {
        query = query.in('status', filters.status);
      }

      if (filters?.priority && filters.priority.length > 0) {
        query = query.in('priority', filters.priority);
      }

      if (filters?.assignedTo && filters.assignedTo.length > 0) {
        query = query.contains('assigned_to', filters.assignedTo);
      }

      const { data, error } = await query;

      if (error) {
        console.error('Error fetching tasks:', error);
        throw error;
      }

      // Parse JSON fields
      const tasks = (data || []).map(task => ({
        ...task,
        assigned_to: Array.isArray(task.assigned_to) ? task.assigned_to : [],
        assigned_names: Array.isArray(task.assigned_names) ? task.assigned_names : [],
        tags: Array.isArray(task.tags) ? task.tags : [],
        attachments: Array.isArray(task.attachments) ? task.attachments : [],
        start_date: new Date(task.start_date),
        end_date: new Date(task.end_date)
      }));

      console.log(`✅ Fetched ${tasks.length} tasks`);
      return tasks;
    } catch (error) {
      console.error('Failed to fetch tasks:', error);
      throw error;
    }
  }

  async createTask(taskData: TaskFormData): Promise<GanttTask> {
    try {
      // Get max order_index for the project
      const { data: maxOrderData } = await supabase
        .from('gantt_tasks')
        .select('order_index')
        .eq('project_id', taskData.project_id)
        .order('order_index', { ascending: false })
        .limit(1);

      const order_index = maxOrderData && maxOrderData[0] 
        ? maxOrderData[0].order_index + 1 
        : 0;

      const { data, error } = await supabase
        .from('gantt_tasks')
        .insert([{
          ...taskData,
          order_index,
          assigned_to: taskData.assigned_to || [],
          assigned_names: taskData.assigned_names || [],
          tags: taskData.tags || [],
          attachments: taskData.attachments || []
        }])
        .select()
        .single();

      if (error) throw error;
      console.log('✅ Task created:', data);
      return data;
    } catch (error) {
      console.error('Failed to create task:', error);
      throw error;
    }
  }

  async updateTask(id: string, updates: Partial<GanttTask>): Promise<GanttTask> {
    try {
      // First check if task is locked by another user
      const userEmail = localStorage.getItem('userEmail') || 'system';
      const { data: currentTask } = await supabase
        .from('gantt_tasks')
        .select('is_locked, locked_by')
        .eq('id', id)
        .single();

      if (currentTask?.is_locked && currentTask.locked_by !== userEmail) {
        throw new Error(`Task is locked by ${currentTask.locked_by}`);
      }

      const { data, error } = await supabase
        .from('gantt_tasks')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      console.log('✅ Task updated:', data);
      return data;
    } catch (error) {
      console.error('Failed to update task:', error);
      throw error;
    }
  }

  async deleteTask(id: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('gantt_tasks')
        .delete()
        .eq('id', id);

      if (error) throw error;
      console.log('✅ Task deleted');
    } catch (error) {
      console.error('Failed to delete task:', error);
      throw error;
    }
  }

  async reorderTasks(taskIds: string[]): Promise<void> {
    try {
      const updates = taskIds.map((id, index) => ({
        id,
        order_index: index
      }));

      for (const update of updates) {
        await supabase
          .from('gantt_tasks')
          .update({ order_index: update.order_index })
          .eq('id', update.id);
      }

      console.log('✅ Tasks reordered');
    } catch (error) {
      console.error('Failed to reorder tasks:', error);
      throw error;
    }
  }

  // ==================== TASK LOCKING ====================
  async lockTask(taskId: string): Promise<boolean> {
    try {
      const userEmail = localStorage.getItem('userEmail') || 'system';
      
      // Use Supabase RPC to call the lock function
      const { data, error } = await supabase
        .rpc('lock_gantt_task', {
          p_task_id: taskId,
          p_user_id: userEmail  // zmieniono z p_user na p_user_id
        });

      if (error) throw error;
      
      if (!data) {
        // Task is locked by another user
        const { data: taskData } = await supabase
          .from('gantt_tasks')
          .select('locked_by')
          .eq('id', taskId)
          .single();
        
        console.warn(`⚠️ Task locked by ${taskData?.locked_by}`);
        return false;
      }

      console.log('🔒 Task locked successfully');
      return true;
    } catch (error) {
      console.error('Failed to lock task:', error);
      return false;
    }
  }

  async unlockTask(taskId: string): Promise<boolean> {
    try {
      const userEmail = localStorage.getItem('userEmail') || 'system';
      
      const { data, error } = await supabase
        .rpc('unlock_gantt_task', {
          p_task_id: taskId,
          p_user_id: userEmail  // zmieniono z p_user na p_user_id
        });

      if (error) throw error;
      console.log('🔓 Task unlocked');
      return data || false;
    } catch (error) {
      console.error('Failed to unlock task:', error);
      return false;
    }
  }

  // ==================== DEPENDENCIES ====================
  async getDependencies(projectId?: string): Promise<GanttDependency[]> {
    try {
      let query = supabase
        .from('gantt_dependencies')
        .select('*');

      if (projectId) {
        // Get task IDs for the project first
        const { data: tasks } = await supabase
          .from('gantt_tasks')
          .select('id')
          .eq('project_id', projectId);

        if (tasks && tasks.length > 0) {
          const taskIds = tasks.map(t => t.id);
          query = query.or(`source_id.in.(${taskIds.join(',')}),target_id.in.(${taskIds.join(',')})`);
        }
      }

      const { data, error } = await query;

      if (error) throw error;
      console.log(`✅ Fetched ${data?.length || 0} dependencies`);
      return data || [];
    } catch (error) {
      console.error('Failed to fetch dependencies:', error);
      return [];
    }
  }

  async createDependency(dependency: Omit<GanttDependency, 'id'>): Promise<GanttDependency> {
    try {
      const { data, error } = await supabase
        .from('gantt_dependencies')
        .insert([dependency])
        .select()
        .single();

      if (error) throw error;
      console.log('✅ Dependency created');
      return data;
    } catch (error) {
      console.error('Failed to create dependency:', error);
      throw error;
    }
  }

  async deleteDependency(id: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('gantt_dependencies')
        .delete()
        .eq('id', id);

      if (error) throw error;
      console.log('✅ Dependency deleted');
    } catch (error) {
      console.error('Failed to delete dependency:', error);
      throw error;
    }
  }

  // ==================== RESOURCES ====================
  async getResources(taskId?: string): Promise<GanttResource[]> {
    try {
      let query = supabase
        .from('gantt_resources')
        .select('*')
        .order('created_at', { ascending: false });

      if (taskId) {
        query = query.eq('task_id', taskId);
      }

      const { data, error } = await query;

      if (error) throw error;
      
      // Parse dates
      const resources = (data || []).map(r => ({
        ...r,
        start_date: r.start_date ? new Date(r.start_date) : undefined,
        end_date: r.end_date ? new Date(r.end_date) : undefined
      }));

      console.log(`✅ Fetched ${resources.length} resources`);
      return resources;
    } catch (error) {
      console.error('Failed to fetch resources:', error);
      return [];
    }
  }

  async createResource(resource: Omit<GanttResource, 'id'>): Promise<GanttResource> {
    try {
      const { data, error } = await supabase
        .from('gantt_resources')
        .insert([resource])
        .select()
        .single();

      if (error) throw error;
      console.log('✅ Resource created');
      return data;
    } catch (error) {
      console.error('Failed to create resource:', error);
      throw error;
    }
  }

  async updateResource(id: string, updates: Partial<GanttResource>): Promise<GanttResource> {
    try {
      const { data, error } = await supabase
        .from('gantt_resources')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      console.log('✅ Resource updated');
      return data;
    } catch (error) {
      console.error('Failed to update resource:', error);
      throw error;
    }
  }

  async deleteResource(id: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('gantt_resources')
        .delete()
        .eq('id', id);

      if (error) throw error;
      console.log('✅ Resource deleted');
    } catch (error) {
      console.error('Failed to delete resource:', error);
      throw error;
    }
  }

  // ==================== USERS & MACHINES ====================
  async getUsers(): Promise<any[]> {
    // Zawsze zwracaj domyślnych użytkowników, aby uniknąć błędów 400
    // Tabele profiles/users mogą nie istnieć lub mieć inne schematy
    console.log('Using default users for Gantt module');
    return [
      { id: 'user1', email: 'jan@plexisystem.pl', full_name: 'Jan Kowalski', role: 'kierownik_produkcji' },
      { id: 'user2', email: 'anna@plexisystem.pl', full_name: 'Anna Nowak', role: 'handlowiec' },
      { id: 'user3', email: 'piotr@plexisystem.pl', full_name: 'Piotr Wiśniewski', role: 'operator' },
      { id: 'user4', email: 'marek@plexisystem.pl', full_name: 'Marek Zieliński', role: 'operator' },
      { id: 'user5', email: 'katarzyna@plexisystem.pl', full_name: 'Katarzyna Lewandowska', role: 'projektant' },
      { id: 'user6', email: 'tomasz@plexisystem.pl', full_name: 'Tomasz Wójcik', role: 'magazynier' }
    ];
  }

  async getMachines(): Promise<any[]> {
    // Zawsze zwracaj domyślne maszyny, aby uniknąć błędów
    console.log('Using default machines for Gantt module');
    return [
      { id: 'machine1', name: 'Laser CO2 #1', type: 'laser', status: 'active', capacity: 100 },
      { id: 'machine2', name: 'Laser CO2 #2', type: 'laser', status: 'active', capacity: 100 },
      { id: 'machine3', name: 'Frezarka CNC', type: 'cnc', status: 'active', capacity: 80 },
      { id: 'machine4', name: 'Gilotyna', type: 'cutting', status: 'active', capacity: 120 },
      { id: 'machine5', name: 'Ploter tnący', type: 'plotter', status: 'active', capacity: 90 },
      { id: 'machine6', name: 'Termoformierka', type: 'thermoforming', status: 'maintenance', capacity: 60 }
    ];
  }

  // ==================== ACTIVE USERS (for multi-user) ====================
  async getActiveUsers(): Promise<any[]> {
    try {
      // Get all locked tasks with user info
      const { data, error } = await supabase
        .from('gantt_tasks')
        .select('id, name, locked_by, locked_at')
        .eq('is_locked', true)
        .not('locked_by', 'is', null);

      if (error) throw error;

      const activeUsers = (data || []).map(task => ({
        user: task.locked_by,
        taskId: task.id,
        taskName: task.name,
        lockedAt: task.locked_at
      }));

      return activeUsers;
    } catch (error) {
      console.error('Failed to fetch active users:', error);
      return [];
    }
  }

  // ==================== AUTO UNLOCK OLD LOCKS ====================
  async autoUnlockOldTasks(): Promise<void> {
    try {
      const { error } = await supabase.rpc('auto_unlock_old_tasks');
      if (error) throw error;
      console.log('🔓 Auto-unlocked old tasks');
    } catch (error) {
      console.error('Failed to auto-unlock tasks:', error);
    }
  }

  // ==================== EXPORT ====================
  async exportData(projectId?: string): Promise<any> {
    try {
      const [projects, tasks, dependencies, resources] = await Promise.all([
        projectId 
          ? supabase.from('gantt_projects').select('*').eq('id', projectId).single()
          : this.getProjects(),
        this.getTasks(projectId),
        this.getDependencies(projectId),
        this.getResources()
      ]);

      return {
        projects: Array.isArray(projects) ? projects : [projects.data],
        tasks,
        dependencies,
        resources,
        exportDate: new Date().toISOString(),
        version: '1.0.0'
      };
    } catch (error) {
      console.error('Failed to export data:', error);
      throw error;
    }
  }
}

export const ganttService = new GanttService();
